import { ApiClient } from "../ApiClient";
import { RequestOptions, Type } from "../domain";

export type Query = object;

export interface EndpointRequestOptions extends Omit<RequestOptions, "body"> {
  body?: unknown;
  query?: Query;
  type?: "json" | "blob";
  signal?: AbortSignal;
}

export type EndpointType = Type<{
  request<T>(subPath?: string, options?: EndpointRequestOptions): Promise<T>;
}>;

type Pojo = string | number | boolean | Date | null | Pojo[] | { [key: string]: Pojo };

const UUID_SEGMENT = /\/[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}(?=\/|$)/gi;
const NUMERIC_SEGMENT = /\/\d+(?=\/|$)/g;

// Les identifiants sont gommés pour que le regroupement se fasse par route et non par ressource.
export const toRouteShape = (pathname: string): string =>
  pathname.replace(UUID_SEGMENT, "/:id").replace(NUMERIC_SEGMENT, "/:id");

export const withRoute = (message: string, method: string | undefined, pathname: string): string =>
  `${message} — ${method ?? "GET"} ${toRouteShape(pathname)}`;

export const setQueryMember = (key: string, value: Pojo, params: URLSearchParams): void => {
  if (value == null) {
    return;
  }
  if (Array.isArray(value)) {
    for (const nested of value) {
      setQueryMember(`${key}[]`, nested, params);
    }
  } else if (value instanceof Date) {
    params.append(key, value.toISOString());
  } else if (typeof value === "object") {
    for (const [nestedKey, nestedValue] of Object.entries(value)) {
      setQueryMember(`${key}[${nestedKey}]`, nestedValue, params);
    }
  } else if (typeof value === "boolean") {
    params.append(key, value ? "true" : "false");
  } else {
    params.append(key, value.toString());
  }
};

export const setQuery = (params: URLSearchParams, query: Query): URLSearchParams => {
  for (const [key, value] of Object.entries(query)) {
    setQueryMember(key, value, params);
  }
  return params;
};

export interface BaseEndpointType {
  readonly client: ApiClient;

  request<T>(subPath?: string, options?: EndpointRequestOptions): Promise<T>;

  resolvePath(subPath: string): string;
}

export const BaseEndpoint = (path: string): Type<BaseEndpointType> =>
  class {
    constructor(readonly client: ApiClient) {}

    resolvePath(subPath: string) {
      return `${path}/${subPath}`;
    }

    async request<T>(subPath = "", { query, body, type, ...options }: EndpointRequestOptions = {}): Promise<T> {
      const resolved = this.resolvePath(subPath);
      const url = new URL(resolved, this.client.apiUrl);

      if (query != null) {
        setQuery(url.searchParams, query);
      }
      if (options.headers == null) {
        options.headers = {};
      }
      let serializedBody: string | FormData | undefined;
      if (body != null) {
        if (body instanceof FormData) {
          serializedBody = body;
        } else {
          options.headers["Content-Type"] = "application/json";
          serializedBody = JSON.stringify(body);
        }
      }
      const response = await this.client.requester(url.href, {
        ...options,
        body: serializedBody,
      });

      if (response.status >= 400) {
        const { message } = await response.json();
        // Un 5xx porte le même message quelle que soit la route, donc Sentry empile les 500 de
        // toute l'API dans une seule issue. Les 4xx gardent leur message intact : il sert de clé
        // dans le catalogue d'erreurs côté web.
        throw new Error(response.status >= 500 ? withRoute(message, options.method, url.pathname) : message);
      } else if (response.status === 204) {
        return undefined as unknown as T;
      } else {
        return !type || type === "json" ? response.json() : (response.blob() as unknown as T);
      }
    }
  };
